import { useQuery } from '@vue/apollo-composable'
import { computed } from '@vue/composition-api'
import GetActiveCustomersQuery from '../graphql/GetActiveCustomersQuery.graphql'

/**
 * Composable to fetch all active customers for a company
 * @param {Ref<String>|String} companyId - Company ID (can be ref or string)
 */
export const useGetActiveCustomers = (companyId) => {
	const { result, loading, error, onResult, onError, refetch } = useQuery(
		GetActiveCustomersQuery,
		() => ({
			company_id: companyId.value || companyId,
		}),
		{
			fetchPolicy: 'network-only',
			enabled: () => !!(companyId.value || companyId),
		},
	)

	// Process the customers data
	const customers = computed(() => {
		if (!result.value || !result.value.customers) return []
		return result.value.customers
	})

	// Get total count of customers
	const customersCount = computed(() => {
		return customers.value.length
	})

	// Execute query manually
	const executeQuery = async (compId) => {
		try {
			const result = await refetch({
				company_id: compId || companyId.value || companyId,
			})
			return result
		} catch (err) {
			console.error('Error fetching active customers:', err)
			throw err
		}
	}

	return {
		customers,
		customersCount,
		loading,
		error,
		onResult,
		onError,
		executeQuery,
		refetch,
	}
}

/**
 * Alternative standalone function to fetch active customers
 * Useful for one-time fetches without reactive updates
 */
export const getActiveCustomers = async (apolloClient, companyId) => {
	try {
		const { data } = await apolloClient.query({
			query: GetActiveCustomersQuery,
			variables: {
				company_id: companyId,
			},
			fetchPolicy: 'network-only',
		})

		if (!data || !data.customers) {
			return []
		}

		return data.customers
	} catch (error) {
		console.error('Error fetching active customers:', error)
		throw error
	}
}

/**
 * Helper function to search/filter customers
 * @param {Array} customers - Array of customer objects
 * @param {String} searchTerm - Search term to filter by
 */
export const filterCustomers = (customers, searchTerm) => {
	if (!searchTerm) return customers

	const term = searchTerm.toLowerCase()
	return customers.filter((customer) => {
		return (
			customer.customer_id.toLowerCase().includes(term) ||
			customer.customer_title.toLowerCase().includes(term) ||
			(customer.customer_nickname &&
				customer.customer_nickname.toLowerCase().includes(term))
		)
	})
}

/**
 * Helper function to sort customers
 * @param {Array} customers - Array of customer objects
 * @param {String} sortBy - Field to sort by (customer_id, customer_title, etc.)
 * @param {String} order - Sort order ('asc' or 'desc')
 */
export const sortCustomers = (
	customers,
	sortBy = 'customer_id',
	order = 'asc',
) => {
	const sorted = [...customers].sort((a, b) => {
		const aVal = a[sortBy] || ''
		const bVal = b[sortBy] || ''

		if (aVal < bVal) return order === 'asc' ? -1 : 1
		if (aVal > bVal) return order === 'asc' ? 1 : -1
		return 0
	})

	return sorted
}
